import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { InlineLoader } from "@/components/common/Loader";
import { OPS } from "@/constants/testIds";
import {
    dkMessage, fmtMoney, fmtPct, fmtQuantity, fmtSigned, isIsoMonth, monthsBack, pctOf, reconcileRows, unitOf,
} from "./dkUtils";
import { ExportButton, LocationPicker, MonthPicker, NoteBox, OfflineBox, cardClass } from "./widgets";

const R = OPS.dk.reconcile;
const ALL = "all";

const q = (v, lang) => (v === null || v === undefined ? "" : fmtQuantity(v, lang));

function Cell({ v, sub, tone = "", testId, money }) {
    return (
        <td data-testid={testId} className={`whitespace-nowrap px-2 py-2 text-center text-xs tabular-nums ${tone || "text-[color:var(--ink)]"}`}>
            {v === "" || v === null || v === undefined ? <span className="text-[color:var(--ink-faint)]">—</span>
                // money carries its currency: an auto-direction isolate keeps «١٢٫٥٠ ر.س» in Arabic order
                : money ? <bdi>{v}</bdi> : <bdi dir="ltr">{v}</bdi>}
            {sub ? <span className="block text-[10px] font-medium text-[color:var(--ink-faint)]">{sub}</span> : null}
        </td>
    );
}

/**
 * «المطابقة الشهرية»: per location × item for a month — opening (the day-1
 * opening override when there is one, else the previous month's stock count;
 * labelled from `opening_ref.source`), received / sent sums, closing (this month's stock count
 * when submitted or approved, else the last daily closing), used, by sales,
 * W&M, the unexplained variance and its flag, and the month-end check (last
 * daily closing vs the stock count). The SAR column exists only when the server
 * sends `value_sar` (finance / view-all).
 */
export function Reconcile({ scope }) {
    const { t, tn, lang } = useI18n();
    const [searchParams, setSearchParams] = useSearchParams();
    const thisMonth = (scope?.today || "").slice(0, 7);
    const months = useMemo(() => monthsBack(thisMonth, 12), [thisMonth]);
    const paramMonth = searchParams.get("month");
    const month = isIsoMonth(paramMonth) && months.includes(paramMonth) ? paramMonth : thisMonth;
    const [locFilter, setLocFilter] = useState(ALL);

    const setMonth = (v) => {
        const next = new URLSearchParams(searchParams);
        next.set("month", v);
        setSearchParams(next, { replace: true });
    };

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const seq = useRef(0);
    const load = useCallback(async () => {
        if (!month) { setLoading(false); return; }
        const my = ++seq.current;
        setLoading(true);
        setError(null);
        try {
            const r = await api.get("/dk/reconcile", { params: { month } });
            if (my === seq.current) setData({ month, data: r.data });
        } catch (e) {
            if (my === seq.current) setError(e);
        } finally {
            if (my === seq.current) setLoading(false);
        }
    }, [month]);
    useEffect(() => { load(); }, [load]);

    const rec = data && data.month === month ? data.data : null;
    const rows = useMemo(() => reconcileRows(rec), [rec]);
    const locations = useMemo(() => {
        const list = rec?.locations || scope?.locations || [];
        const used = new Set(rows.map((r) => r.loc));
        return list.filter((l) => used.has(l.id));
    }, [rec, scope, rows]);
    const items = useMemo(() => rec?.items || [], [rec]);
    const itemById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
    const itemRank = useMemo(() => new Map(items.map((i, n) => [i.id, n])), [items]);
    // the server says whether values are shown (`values_visible`); else a returned `value_sar` does
    const showSar = typeof rec?.values_visible === "boolean" ? rec.values_visible : rows.some((r) => r.value_sar !== undefined);
    useEffect(() => { if (locFilter !== ALL && !locations.some((l) => l.id === locFilter)) setLocFilter(ALL); }, [locations, locFilter]);

    const groups = useMemo(() => locations
        .filter((l) => locFilter === ALL || l.id === locFilter)
        .map((l) => [l, rows.filter((r) => r.loc === l.id)
            .sort((a, b) => (itemRank.get(a.item) ?? 99) - (itemRank.get(b.item) ?? 99))]), [locations, rows, locFilter, itemRank]);
    const sarTotal = showSar
        ? groups.reduce((s, [, list]) => s + list.reduce((x, r) => x + (typeof r.value_sar === "number" ? r.value_sar : 0), 0), 0)
        : null;
    const msg = error ? dkMessage(error, lang) : null;
    const th = "whitespace-nowrap px-2 py-2 text-center text-[11px] font-bold text-[color:var(--ink-muted)]";
    const cols = 11 + (showSar ? 1 : 0);
    // closing_source is stock_count | daily | none: «none» (no closing at all) has no label
    const closingSrc = (s) => (s === "stock_count" ? t("dk_rec_closing_src_count") : s === "daily" ? t("dk_rec_closing_src_daily") : "");
    // opening: the day-1 override, else last month's stock count (opening_ref.source; none -> no label)
    const openingSrc = (r) => {
        const s = r.opening_ref?.source || r.opening_source;
        return s === "override" ? t("dk_rec_opening_src_override") : s === "stock_count" ? t("dk_rec_opening_src_count") : "";
    };

    return (
        <div data-testid={R.view} className="space-y-3">
            <div className={`${cardClass} space-y-2 p-3`}>
                <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto] sm:items-center">
                    <MonthPicker months={months} value={month} onChange={setMonth} />
                    <LocationPicker locations={locations} value={locFilter} onChange={setLocFilter} allowAll />
                    <ExportButton month={month} testId={R.exportBtn} />
                </div>
                {showSar ? (
                    <p data-testid={R.sarTotal} data-value={Math.round((sarTotal || 0) * 100) / 100}
                       className="text-xs font-bold text-[color:var(--brand-brown)]">
                        {t("dk_rec_sar_total")}: <bdi>{fmtMoney(sarTotal || 0, lang)}</bdi>
                    </p>
                ) : null}
            </div>

            {error && !rec ? (msg ? <NoteBox>{msg}</NoteBox> : <OfflineBox onRetry={load} busy={loading} />) : null}

            {loading && !rec ? <InlineLoader /> : rec ? (
                !groups.length ? (
                    <p data-testid={R.empty}
                       className="rounded-[var(--radius-lg)] border border-dashed border-[color:var(--border-soft)] py-10 text-center text-sm text-[color:var(--ink-muted)]">
                        {t("dk_rec_empty")}
                    </p>
                ) : (
                    <div className={`overflow-x-auto rounded-[var(--radius-lg)] border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] transition-opacity duration-200 ${loading ? "opacity-60" : ""}`}>
                        <table data-testid={R.table} data-sar={showSar ? "true" : "false"} className="w-max min-w-full border-collapse text-sm">
                            <thead>
                                <tr className="bg-[color:var(--bg-surface-2)]">
                                    <th className="sticky z-20 min-w-[7rem] bg-[color:var(--bg-surface-2)] px-3 py-2 text-start text-[11px] font-bold text-[color:var(--ink-muted)]"
                                        style={{ insetInlineStart: 0 }}>{t("dk_item")}</th>
                                    <th className={th}>{t("dk_rec_opening")}</th>
                                    <th className={th}>{t("dk_received")}</th>
                                    <th className={th}>{t("dk_sent")}</th>
                                    <th className={th}>{t("dk_rec_closing")}</th>
                                    <th className={th}>{t("dk_used")}</th>
                                    <th className={th}>{t("dk_theo")}</th>
                                    <th className={th}>{t("dk_rec_explained")}</th>
                                    <th className={th}>{t("dk_variance")}</th>
                                    <th className={th}>{t("dk_rec_last_daily")}</th>
                                    <th className={th}>{t("dk_rec_month_end_diff")}</th>
                                    {showSar ? <th className={th}>{t("dk_rec_value_sar")}</th> : null}
                                </tr>
                            </thead>
                            <tbody>
                                {groups.map(([loc, list]) => (
                                    <Fragment key={loc.id}>
                                        <tr className="border-t-2 border-[color:var(--border-soft)] bg-[color:var(--bg-surface-2)]">
                                            <td colSpan={cols} className="px-0 py-0">
                                                <span className="sticky block w-max px-3 py-1.5 text-xs font-extrabold text-[color:var(--brand-brown)]"
                                                      style={{ insetInlineStart: 0 }}>{tn(loc)}</span>
                                            </td>
                                        </tr>
                                        {list.map((r) => {
                                            const it = itemById.get(r.item) || { id: r.item };
                                            const unit = unitOf(it, t);
                                            const pct = pctOf(r.unexplained, r.theoretical, r.pct);
                                            const diff = r.month_end_diff;
                                            return (
                                                <tr key={`${r.loc}|${r.item}`} data-testid={R.row(r.loc, r.item)} data-flag={r.flag ? "true" : "false"}
                                                    className="border-t border-[color:var(--separator)]">
                                                    <td className="sticky z-10 bg-[color:var(--bg-surface)] px-3 py-2 text-start" style={{ insetInlineStart: 0 }}>
                                                        <span className="block truncate text-xs font-semibold text-[color:var(--ink)]">{tn(it) || r.item}</span>
                                                        <span className="text-[10px] text-[color:var(--ink-faint)]">{unit}</span>
                                                    </td>
                                                    <Cell v={q(r.opening, lang)} sub={openingSrc(r)} />
                                                    <Cell v={q(r.received, lang)} />
                                                    <Cell v={r.sent ? q(r.sent, lang) : ""} />
                                                    <Cell v={q(r.closing, lang)} sub={closingSrc(r.closing_source)} />
                                                    <Cell v={q(r.used, lang)} />
                                                    <Cell v={q(r.theoretical, lang)} />
                                                    {/* 0 = no W&M recorded; «—» = W&M data not available */}
                                                    <Cell v={q(r.explained, lang)} />
                                                    <td className={`whitespace-nowrap px-2 py-2 text-center text-xs font-extrabold tabular-nums ${
                                                        r.flag ? "bg-[color:var(--danger-soft)] text-[color:var(--danger-soft-fg)]" : "text-[color:var(--ink)]"}`}>
                                                        <span className="inline-flex items-center gap-0.5">
                                                            {r.flag ? <AlertTriangle className="h-3 w-3" /> : null}
                                                            {r.unexplained === null || r.unexplained === undefined
                                                                ? <span className="text-[color:var(--ink-faint)]">—</span>
                                                                : <bdi dir="ltr">{fmtSigned(r.unexplained, lang)}</bdi>}
                                                        </span>
                                                        {pct !== null ? <span className="block text-[10px] font-semibold"><bdi dir="ltr">{fmtPct(pct, lang)}</bdi></span> : null}
                                                    </td>
                                                    <Cell v={q(r.last_daily_closing, lang)}
                                                          sub={r.stock_count_closing === null || r.stock_count_closing === undefined ? ""
                                                              : `${t("dk_rec_stock_count")}: ${q(r.stock_count_closing, lang)}`} />
                                                    <Cell v={diff === null || diff === undefined ? "" : fmtSigned(diff, lang)}
                                                          tone={diff ? "font-bold text-[color:var(--warning-soft-fg)]" : ""} />
                                                    {showSar ? (
                                                        <Cell testId={R.sar(r.loc, r.item)} money
                                                              v={r.value_sar === null || r.value_sar === undefined ? "" : fmtMoney(r.value_sar, lang)} />
                                                    ) : null}
                                                </tr>
                                            );
                                        })}
                                    </Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            ) : null}

            {rec ? <p className="text-[11px] leading-relaxed text-[color:var(--ink-faint)]">{t("dk_rec_legend")}</p> : null}
        </div>
    );
}
